import { Link } from 'react-router-dom';
import type { SubcontractRequestListItem } from '@/lib/apiClient';
import { fmtDateTime } from '@/lib/dates';
import { jobHref } from './contractsModel';
import { requestHref, requestStanding } from './subcontractRequestModel';

const dash = <span className="ct-none">—</span>;

/**
 * Subcontract requests (#627) as a table: the coordinator's queue, and a job's own requests on its Contracts
 * tab. A list carries no dollar amount (the API leaves it out of every list), so there is none to show here.
 *
 * `showRequester` is the queue's: the coordinator needs to know who to ask. A requester looking at their own
 * requests does not need their own name beside each one.
 */
export default function SubcontractRequestList({ title, titleId, items, showJob, showRequester, empty }: {
  title: string;
  /** Unique on the page, so a screen reader can name the section. */
  titleId: string;
  items: SubcontractRequestListItem[];
  showJob: boolean;
  showRequester: boolean;
  empty: string;
}) {
  return (
    <section className="ct-section" aria-labelledby={titleId}>
      <header className="ct-section-head">
        <h2 className="ct-section-title" id={titleId}>{title}</h2>
        <span className="ct-section-count">{items.length}</span>
      </header>
      {items.length === 0 ? (
        <div className="ct-empty">{empty}</div>
      ) : (
        <div className="ew-table-scroll">
          <table className="ct-table ct-table--requests">
            <thead>
              <tr>
                {showJob && <th scope="col" className="ct-w-job">Job</th>}
                <th scope="col">Vendor</th>
                <th scope="col" className="ct-w-tpl">Template</th>
                {showRequester && <th scope="col" className="ct-w-person">Requested by</th>}
                <th scope="col" className="ct-w-when">{showRequester ? 'Submitted' : 'Last changed'}</th>
                <th scope="col" className="ct-w-stand">Where it stands</th>
                <th scope="col" className="ct-w-act"><span className="ct-sr">Open</span></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const standing = requestStanding(r);
                const when = showRequester ? r.submittedAt : (r.updatedAt ?? r.createdAt);
                const who = r.vendorName ?? 'a vendor not named yet';
                return (
                  <tr key={r.id}>
                    {showJob && (
                      <td className="ct-job">
                        <Link to={jobHref(r.projectId)}>{r.jobNumber}</Link>
                        {r.projectTitle && <span className="ct-under">{r.projectTitle}</span>}
                      </td>
                    )}
                    <td>{r.vendorName ?? <span className="ct-none">Not named yet</span>}</td>
                    <td>{r.templateLabel}</td>
                    {showRequester && <td>{r.requestedBy.name ?? dash}</td>}
                    <td className="ct-date">{when ? fmtDateTime(when) : dash}</td>
                    <td>
                      <span className={`ct-chip ct-chip--${standing.tone}`}>{standing.label}</span>
                    </td>
                    <td>
                      <Link className="ew-btn-outline" to={requestHref(r.id)} aria-label={`Open the request for ${who}`}>Open</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
